import * as GC from '@mescius/spread-sheets';

import {
  collectUniqueLoops,
  findLoopsIntersectingColumns,
  findLoopsIntersectingRange,
  findLoopsIntersectingRows,
  removeLoopById,
} from './loop';
import type { LoopRange } from './loop';
import { translate } from './translations';

type ConfirmFn = (params: { message: string }) => Promise<boolean>;

type SafeDragDropCommandOptions = {
  sheetName: string;
  fromRow: number;
  fromCol: number;
  toRow: number;
  toCol: number;
  rowCount: number;
  colCount: number;
  copy: boolean;
  insert: boolean;
  copyOption: GC.Spread.Sheets.CopyToOptions;
  loopIds: number[];
};

function registerSafeDragDropCommand(spread: GC.Spread.Sheets.Workbook): void {
  const commandManager = spread.commandManager();

  commandManager.register('safeDragDrop', {
    canUndo: true,
    execute: (
      context: GC.Spread.Sheets.Workbook,
      options: SafeDragDropCommandOptions,
      isUndo: boolean
    ): boolean => {
      const Commands = GC.Spread.Sheets.Commands;

      if (isUndo) {
        Commands.undoTransaction(context, options);
        return true;
      }

      const sheet = context.getSheetFromName(options.sheetName);
      if (!sheet) {
        return false;
      }

      Commands.startTransaction(context, options);

      try {
        for (const loopId of options.loopIds) {
          removeLoopById(sheet, loopId);
        }

        context.commandManager().execute({
          cmd: 'dragDrop',
          sheetName: options.sheetName,
          fromRow: options.fromRow,
          fromColumn: options.fromCol,
          toRow: options.toRow,
          toColumn: options.toCol,
          rowCount: options.rowCount,
          columnCount: options.colCount,
          copy: options.copy,
          insert: options.insert,
          option: options.copyOption,
        });

        Commands.endTransaction(context, options);
        return true;
      } catch (error) {
        Commands.undoTransaction(context, options);
        throw error;
      }
    },
  });
}

/**
 * ドラッグ&ドロップ（行・列・セルの移動）が行われた際のハンドラー
 */
export function registerDragDropHandler(
  spread: GC.Spread.Sheets.Workbook,
  confirm: ConfirmFn
): void {
  // 関連するカスタムコマンドの登録
  registerSafeDragDropCommand(spread);

  spread.bind(
    GC.Spread.Sheets.Events.DragDropBlock,
    async (
      _e: unknown,
      args: GC.Spread.Sheets.IDragDropBlockEventArgs
    ): Promise<void> => {
      const sheet = args.sheet;
      if (!sheet) return;

      const {
        fromRow,
        fromCol,
        toRow,
        toCol,
        rowCount,
        colCount,
        copy,
        insert,
        copyOption,
      } = args;

      let intersectingLoops: LoopRange[] = [];
      let message = '';

      // 行移動
      if (fromCol === -1 && toCol === -1) {
        intersectingLoops = collectUniqueLoops([
          ...findLoopsIntersectingRows(sheet, fromRow, rowCount),
          ...findLoopsIntersectingRows(sheet, toRow, rowCount),
        ]);
        message = translate(
          'MOVE_ROW_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE'
        );
      }

      // 列移動
      if (fromRow === -1 && toRow === -1) {
        intersectingLoops = collectUniqueLoops([
          ...findLoopsIntersectingColumns(sheet, fromCol, colCount),
          ...findLoopsIntersectingColumns(sheet, toCol, colCount),
        ]);
        message = translate(
          'MOVE_COLUMN_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE'
        );
      }

      // セル移動
      if (fromCol !== -1 && toCol !== -1 && fromRow !== -1 && toRow !== -1) {
        const fromRange = new GC.Spread.Sheets.Range(
          fromRow,
          fromCol,
          rowCount,
          colCount
        );

        const toRange = new GC.Spread.Sheets.Range(
          toRow,
          toCol,
          rowCount,
          colCount
        );
        intersectingLoops = collectUniqueLoops([
          ...findLoopsIntersectingRange(sheet, fromRange),
          ...findLoopsIntersectingRange(sheet, toRange),
        ]);
        message = translate(
          'MOVE_CELL_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE'
        );
      }

      if (intersectingLoops.length === 0) {
        return;
      }

      // 先に必ず止める
      args.cancel = true;

      const ok = await confirm({ message });
      if (!ok) {
        return;
      }

      spread.commandManager().execute({
        cmd: 'safeDragDrop',
        sheetName: args.sheetName,
        fromRow,
        fromCol,
        toRow,
        toCol,
        rowCount,
        colCount,
        copy,
        insert,
        copyOption,
        loopIds: intersectingLoops.map((loop) => loop.loopId),
      });
    }
  );
}
